import { Controller } from "@hotwired/stimulus";

// Heroicons-style 16px glyphs used to mark directory vs file entries
// in the dropdown. Inlined so the controller has no runtime asset
// dependency beyond Stimulus + Turbo.
const FOLDER_SVG = '<svg class="w-3.5 h-3.5 shrink-0" style="color: var(--accent);" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M3.75 3A1.75 1.75 0 0 0 2 4.75v10.5C2 16.216 2.784 17 3.75 17h12.5A1.75 1.75 0 0 0 18 15.25v-8.5A1.75 1.75 0 0 0 16.25 5h-6.69l-1.06-1.06A1.75 1.75 0 0 0 7.26 3H3.75Z"/></svg>';
const FILE_SVG   = '<svg class="w-3.5 h-3.5 shrink-0" style="color: var(--muted);" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M4 4a2 2 0 0 1 2-2h5.293A2 2 0 0 1 12.707 2.586l2.707 2.707A2 2 0 0 1 16 6.707V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Zm7 1V3.414L13.586 6H12a1 1 0 0 1-1-1Z" clip-rule="evenodd"/></svg>';

// FileAutocompleteController wires an input to a server-rendered list of
// matching repo file paths. Mounted on a relative-positioned wrapper that
// contains the input and an absolutely-positioned <ul> dropdown. The
// backend endpoint is configured via data-file-autocomplete-url-value
// and is expected to return { matches: ["path/a.go", "path/b.go"] }.
// Entries ending in '/' are treated as directories: shown with a folder
// icon, and Tab (or mouse-down) completes the input to that path while
// keeping the dropdown open so the user can dive deeper.
export default class FileAutocompleteController extends Controller {
  static targets = ["input", "list"];
  static values  = { url: String };

  connect() {
    this.activeIndex = -1;
    this.lastQuery   = null;
    this.debounce    = null;
    this.fetchSeq    = 0;
    this.hideListener = (e) => {
      if (!this.element.contains(e.target)) this.hide();
    };
    document.addEventListener("click", this.hideListener);
    this.stackListener = () => this.onStackChange();
    this.stackSelect()?.addEventListener("change", this.stackListener);
  }

  disconnect() {
    document.removeEventListener("click", this.hideListener);
    this.stackSelect()?.removeEventListener("change", this.stackListener);
    if (this.debounce) clearTimeout(this.debounce);
  }

  // stackSelect is the form's stack dropdown. The file index is per stack,
  // so every fetch names the stack the submitted path will be read from.
  stackSelect() {
    return this.element.closest("form")?.querySelector('select[name="stack"]');
  }

  onStackChange() {
    // The cached query was answered for the previous stack.
    this.lastQuery = null;
    if (this.listTarget.classList.contains("hidden")) return;
    this.fetch(this.inputTarget.value);
  }

  onInput() {
    const q = this.inputTarget.value;
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.fetch(q), 150);
  }

  onFocus() {
    // Show suggestions immediately on focus, even with an empty value —
    // gives users a starting point if they don't know the layout.
    this.fetch(this.inputTarget.value);
  }

  onKeyDown(e) {
    const items = this.items();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (items.length === 0) { this.fetch(this.inputTarget.value); return; }
      this.setActive((this.activeIndex + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      this.setActive((this.activeIndex - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      if (this.activeIndex >= 0 && items[this.activeIndex]) {
        e.preventDefault();
        this.pick(items[this.activeIndex].dataset.path);
      }
    } else if (e.key === "Tab" && !e.shiftKey) {
      // Bash-style completion: when the dropdown is visible, Tab picks the
      // highlighted entry; if nothing is highlighted yet, it picks the
      // first one. Shift-Tab and a hidden dropdown both fall through to
      // default focus navigation. Directories (trailing '/') keep the
      // dropdown open and refetch so the user can dive in; files close.
      if (this.listTarget.classList.contains("hidden") || items.length === 0) return;
      const i = this.activeIndex >= 0 ? this.activeIndex : 0;
      e.preventDefault();
      const path = items[i].dataset.path;
      if (path.endsWith("/")) {
        this.completeAndContinue(path);
      } else {
        this.pick(path);
      }
    } else if (e.key === "Escape") {
      this.hide();
    }
  }

  async fetch(q) {
    if (q === this.lastQuery && this.listTarget.children.length > 0) {
      this.show();
      return;
    }
    this.lastQuery = q;
    const seq = ++this.fetchSeq;
    const stack = this.stackSelect()?.value ?? "";
    const url = `${this.urlValue}?q=${encodeURIComponent(q)}&stack=${encodeURIComponent(stack)}`;
    let data;
    try {
      const res = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(res.status);
      data = await res.json();
    } catch (err) {
      this.hide();
      return;
    }
    // Skip late responses from out-of-order requests.
    if (seq !== this.fetchSeq) return;
    this.render(data.matches || [], q, !!data.loading, data.error || "");
  }

  render(matches, q, loading, error) {
    this.listTarget.replaceChildren();
    if (error) {
      // The index is not coming: show why instead of an eternal
      // "Indexing repository…". Full text on hover; clamped in the dropdown.
      const li = document.createElement("li");
      li.className = "px-3 py-1.5 text-xs italic text-red-600 dark:text-red-400 whitespace-pre-line line-clamp-3 break-words cursor-default";
      li.title = error;
      li.textContent = "Indexing failed: " + error;
      this.listTarget.appendChild(li);
      this.show();
      return;
    }
    if (matches.length === 0) {
      if (loading) {
        const li = document.createElement("li");
        li.className = "px-3 py-1.5 text-xs italic text-gray-500 dark:text-gray-400";
        li.textContent = "Indexing repository…";
        this.listTarget.appendChild(li);
        this.show();
        return;
      }
      this.hide();
      return;
    }

    const lq = q.toLowerCase();
    for (const path of matches) {
      const isDir = path.endsWith("/");
      const li = document.createElement("li");
      li.dataset.path = path;
      li.dataset.dir  = isDir ? "1" : "";
      li.className = "fa-item px-3 py-1.5 text-sm font-mono cursor-pointer flex items-center gap-2" + (isDir ? " is-dir" : "");
      li.innerHTML = (isDir ? FOLDER_SVG : FILE_SVG) +
        '<span class="truncate">' + highlight(path, lq) + "</span>";
      li.addEventListener("mousedown", (e) => {
        // mousedown fires before the input's blur, so the pick happens
        // while the input still has focus — no flicker from hide()/show().
        e.preventDefault();
        if (isDir) this.completeAndContinue(path); else this.pick(path);
      });
      this.listTarget.appendChild(li);
    }
    this.activeIndex = -1;
    this.show();
  }

  pick(path) {
    this.inputTarget.value = path;
    this.hide();
    this.inputTarget.focus();
  }

  // completeAndContinue fills the input with the highlighted directory
  // and immediately refetches so the dropdown reveals its contents. The
  // user can keep arrowing/Tab-ing to drill deeper without losing focus.
  completeAndContinue(path) {
    this.inputTarget.value = path;
    this.inputTarget.focus();
    // Place caret at end so further typing extends the path.
    const end = path.length;
    this.inputTarget.setSelectionRange(end, end);
    this.fetch(path);
  }

  show() { this.listTarget.classList.remove("hidden"); }
  hide() { this.listTarget.classList.add("hidden"); this.activeIndex = -1; }

  items() { return Array.from(this.listTarget.children); }
  setActive(i) {
    const items = this.items();
    items.forEach((el, idx) => {
      el.classList.toggle("is-active", idx === i);
    });
    this.activeIndex = i;
    if (items[i]) items[i].scrollIntoView({ block: "nearest" });
  }
}

function escapeHTML(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// highlight wraps every case-insensitive occurrence of q in path with a
// <strong>. Safe against HTML injection via escapeHTML on the segments.
function highlight(path, lq) {
  if (!lq) return escapeHTML(path);
  const lower = path.toLowerCase();
  let out = "";
  let i = 0;
  while (i < path.length) {
    const idx = lower.indexOf(lq, i);
    if (idx < 0) { out += escapeHTML(path.slice(i)); break; }
    out += escapeHTML(path.slice(i, idx));
    out += "<strong class=\"fa-hit\">" + escapeHTML(path.slice(idx, idx + lq.length)) + "</strong>";
    i = idx + lq.length;
  }
  return out;
}
